/**
 * Météo de l'accueil (carte « Météo », GET /v1/meteo, outil MCP agora_meteo) – règles partagées API / front.
 * Décision du 27/10/2026 : Open-Meteo (AWS ne propose pas de météo ; l'offre Google Weather reste possible, le contrat
 * de l'API ne dépend pas du fournisseur). Le lieu est donné en coordonnées ARRONDIES au dixième de degré (≈ 10 km) :
 * la position précise de l'utilisateur ne quitte pas son navigateur, et le cache est partagé par une même zone.
 * Libellés : `accueil.meteoConditions.<condition>` du dictionnaire (6 langues).
 */
import type { Filiale } from './filiales';

/** Conditions affichées (indépendantes du fournisseur). */
export const CONDITIONS_METEO = [
  'clair', 'peuNuageux', 'eclaircies', 'couvert', 'brouillard', 'bruine', 'pluie', 'averses', 'neige', 'orage',
] as const;
export type ConditionMeteo = (typeof CONDITIONS_METEO)[number];

/** Pictogramme du design (4 dessins de WeatherWidget) de chaque condition ; la neige garde le nuage. */
export const PICTO_METEO: Readonly<Record<ConditionMeteo, 'sun' | 'partly' | 'cloud' | 'rain'>> = {
  clair: 'sun', peuNuageux: 'sun', eclaircies: 'partly', couvert: 'cloud', brouillard: 'cloud',
  bruine: 'rain', pluie: 'rain', averses: 'rain', neige: 'cloud', orage: 'rain',
};

/** Fournisseurs de données : nom affiché dans la mention de source (licence CC BY 4.0 d'Open-Meteo) et lien. */
export const SOURCES_METEO = {
  'open-meteo': { nom: 'Open-Meteo', url: 'https://open-meteo.com/' },
} as const;
export type SourceMeteo = keyof typeof SOURCES_METEO;

/** Code temps présent de l'OMM (table 4677, celle d'Open-Meteo) → condition AGORA. */
export function conditionWmo(code: number): ConditionMeteo {
  if (code === 0) return 'clair';
  if (code === 1) return 'peuNuageux';
  if (code === 2) return 'eclaircies';
  if (code === 3) return 'couvert';
  if (code === 45 || code === 48) return 'brouillard';
  if (code >= 51 && code <= 57) return 'bruine';
  if (code >= 61 && code <= 67) return 'pluie';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'neige';
  if (code >= 80 && code <= 82) return 'averses';
  if (code >= 95) return 'orage';
  return 'couvert';
}

/** Arrondi au dixième de degré (≈ 10 km) : seule précision envoyée à l'API, clé du cache. */
export const arrondirCoordonnee = (x: number): number => Math.round(x * 10) / 10;

/** Fraîcheur des données : rafraîchies toutes les 30 min (design WeatherWidget). */
export const FRAICHEUR_METEO_MS = 30 * 60_000;

/** Unités d'affichage : impériales (°F, mph) pour la filiale des États-Unis, métriques ailleurs. */
export type UnitesMeteo = 'metriques' | 'imperiales';
export const unitesMeteo = (f: Pick<Filiale, 'pays'> | undefined): UnitesMeteo => (f?.pays === 'US' ? 'imperiales' : 'metriques');

/** L'API renvoie des °C et des km/h ; conversion pour l'affichage. */
export const temperatureEn = (celsius: number, unites: UnitesMeteo): number => (unites === 'imperiales' ? (celsius * 9) / 5 + 32 : celsius);
export const ventEn = (kmh: number, unites: UnitesMeteo): number => (unites === 'imperiales' ? kmh / 1.609344 : kmh);
